import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ClickAwayListener, Grow, Paper, MenuItem, MenuList, Tooltip } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import { emptyString } from 'constants/defaultValues';
import { SplitButtonGroup, SplitButtonHandle, SplitButtonPopper, SplitButtonPopperAction, SplitButtonWrapper } from './ButtonStyles';

export const SplitButton = ({ children, actions = [], color = 'primary' }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const paperRef = useRef(null);
  const { t } = useTranslation();

  const handleToggle = useCallback(() => setOpen(prevOpen => !prevOpen), []);

  const handleClose = useCallback(event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  }, []);

  const setPaperMenuStyles = useCallback(() => {
    const anchor = anchorRef.current;
    const paper = paperRef.current;
    if (!anchor || !paper) return;
    paper.style.minWidth = `calc(${anchor.offsetWidth}px - 0.5rem)`;
  }, []);

  const handleAction = useCallback(
    handler => () => {
      setOpen(false);
      handler && handler();
    },
    []
  );

  useEffect(() => {
    window.addEventListener('resize', setPaperMenuStyles);
    return () => {
      window.removeEventListener('resize', setPaperMenuStyles);
    };
  }, [setPaperMenuStyles]);

  const setPaperRef = useCallback(
    ref => {
      paperRef.current = ref;
      setPaperMenuStyles();
    },
    [setPaperMenuStyles]
  );

  const hasVisibleActions = useMemo(() => actions.some(x => x.visible), [actions]);

  return (
    <SplitButtonWrapper container direction='column' alignItems='center'>
      <SplitButtonGroup variant='contained' ref={anchorRef} color={color}>
        {children}
        <SplitButtonHandle size='small' disabled={!hasVisibleActions} color={color} onClick={handleToggle}>
          <Tooltip title={!hasVisibleActions ? t('Tooltips.NoActions') : emptyString}>
            <ArrowDropDownIcon />
          </Tooltip>
        </SplitButtonHandle>
      </SplitButtonGroup>
      <SplitButtonPopper open={open} anchorEl={anchorRef.current} transition>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper ref={setPaperRef}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList>
                  {actions
                    .filter(x => x.visible)
                    .map((action, index) => (
                      <MenuItem key={index} onClick={handleAction(action.handler)}>
                        <SplitButtonPopperAction>
                          {action.icon} {action.name}
                        </SplitButtonPopperAction>
                      </MenuItem>
                    ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </SplitButtonPopper>
    </SplitButtonWrapper>
  );
};

SplitButton.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  actions: PropTypes.array,
  color: PropTypes.string
};

export default SplitButton;
